import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import type {
  SearchRefinementInput,
  SearchSortDirection,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import {
  PlayerPlayingStatus,
  SearchFaceting,
  SearchOperator,
  So5LeaderboardSeasonality,
} from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { InstantCardSearch } from '@sorare/core/src/components/search/InstantSearch';
import type {
  AnalyticTag,
  Props as InstantCardSearchProps,
} from '@sorare/core/src/components/search/InstantSearch/types';
import {
  useConfigContext,
  type PrintablePlayerSortName,
} from '@sorare/core/src/contexts/config';
import { GQLSearchContext } from '@sorare/core/src/contexts/graphql-search';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useDefaultAverageLimit } from '@sorare/core/src/hooks/useDefaultAverageLimit.ts';
import { useVirtualToggle } from '@sorare/core/src/hooks/useVirtualToggle';
import { FILTERS, VIRTUAL_TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import { CurrentMarketplaceUserProvider } from '#src/contexts/currentMarketplaceUser/Provider.tsx';
import { useCurrentMarketplaceUserContext } from '#src/contexts/currentMarketplaceUser/index.ts';
import {
  DEFAULT_PRINTABLE_PLAYER_FACET_VALUES_SEARCH_QUERY,
  useDefaultPrintablePlayersSearchClient,
} from '#src/hooks/search/useDefaultPrintablePlayersSearchClient.ts';
import { FIELDS } from '#src/hooks/search/usePrintablePlayersSearchClient.ts';
import type { extractQueryGeneric } from '#src/lib/searchClient.ts';
import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

import {
  BackendPlayerSearchLayout,
  PlayerSearchLayout,
} from './PlayerSearchLayout.tsx';
import type {
  AdvancedPlayersSearchQuery,
  AdvancedPlayersSearchQueryVariables,
} from './__generated__/index.graphql.ts';
import { BACKEND_FILTERS } from './filters.tsx';
import type { Props } from './types.ts';

export interface FullProps extends Props {
  defaultSort: PrintablePlayerSortName;
  sorts: PrintablePlayerSortName[];
  sport: Sport;
  defaultFilters?: string[];
  getOptionalFilters?: InstantCardSearchProps['getOptionalFilters'];
  analyticsTags: AnalyticTag[];
  defaultHitsPerPage?: number;
  so5FixtureSlug?: string;
  fetchPolicy?: 'network-only' | 'cache-first';
  withStartersOn?: boolean;
}

const ADVANCED_PLAYERS_SEARCH_QUERY = gql`
  query AdvancedPlayersSearchQuery(
    $query: String!
    $page: Int
    $pageSize: Int
    $advancedFilters: String
    $sorts: [SearchSortInput!]!
    $refinements: [SearchRefinementInput!]!
    $facets: [SearchFacetDefinitionInput!]!
    $rarity: Rarity
    $inSeason: Boolean
    $onlyPrimary: Boolean!
    $averageLimit: AverageLimit!
    $onlyFollowed: Boolean
    $so5FixtureSlug: String
    $withoutSo5Fixture: Boolean!
  ) {
    searchPlayers(
      query: $query
      page: $page
      pageSize: $pageSize
      sorts: $sorts
      advancedFilters: $advancedFilters
      refinements: $refinements
      facets: $facets
      onlyFollowed: $onlyFollowed
    ) {
      ...useDefaultPrintablePlayersSearchClient_searchPrintablePlayers
      commonPlayerHits {
        id
        anyPlayer {
          slug
          ...BackendPlayerSearchLayout_AnyPlayerInterface
        }
      }
    }
  }
  ${useDefaultPrintablePlayersSearchClient.fragments.searchPrintablePlayers}
  ${BackendPlayerSearchLayout.fragments.AnyPlayerInterface}
` as TypedDocumentNode<
  AdvancedPlayersSearchQuery,
  AdvancedPlayersSearchQueryVariables
>;

type Query = typeof ADVANCED_PLAYERS_SEARCH_QUERY;

const FavoriteFilterChangeNotifier = ({
  onChange,
}: {
  onChange: (v: boolean) => void;
}) => {
  const { currentRefinement } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.favoriteFilter.name,
  });

  useEffect(() => {
    if (currentRefinement !== undefined) onChange(currentRefinement);
  }, [currentRefinement, onChange]);

  return null;
};

export const AdvancedPlayerSearch = ({
  children,
  sport,
  analyticsTags,
  defaultFilters,
  getOptionalFilters,
  defaultSort,
  initialIndexUIState,
  cardFilters,
  dropdownFilters,
  defaultHitsPerPage,
  so5FixtureSlug = '',
  fetchPolicy,
  withStartersOn = true,
  favoritesWatchlist = false,
  ...rest
}: FullProps) => {
  const { statsManager } = useManagePlayerSearchView();
  const defaultAverageLimit = useDefaultAverageLimit();
  const index = defaultSort || rest.sorts[0];
  const [onlyFollowed, setOnlyFollowed] = useState(favoritesWatchlist);
  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();
  const searchClient = useDefaultPrintablePlayersSearchClient<Query>(
    ADVANCED_PLAYERS_SEARCH_QUERY,
    {
      onlyFollowed,
      fetchPolicy,
      variables: {
        rarity: statsManager[PlayerSearchViewAttributes.RARITY],
        inSeason:
          statsManager[PlayerSearchViewAttributes.SEASONALITY] ===
          So5LeaderboardSeasonality.IN_SEASON,
        onlyPrimary: false,
        averageLimit: defaultAverageLimit,
        so5FixtureSlug,
        withoutSo5Fixture: !so5FixtureSlug,
      },
    },
    (data: extractQueryGeneric<Query>) => data.searchPlayers
  );

  return (
    <ConditionalWrapper
      wrap={!currentMarketplaceUserContext}
      Wrapper={CurrentMarketplaceUserProvider}
      props={{
        sport,
        withMyBidsBanner: false,
      }}
    >
      <SearchCardsContextProvider commonMarket legacyFilters={false}>
        <InstantCardSearch
          {...(index && { indexes: [index] })}
          availableSorts={rest.sorts}
          analyticsTags={analyticsTags}
          sport={sport}
          defaultFilters={defaultFilters}
          getOptionalFilters={getOptionalFilters}
          urlState
          distinct
          {...(withStartersOn && {
            initialIndexUIState: {
              refinementList: {
                [FILTERS.playingStatus.attribute]: [
                  PlayerPlayingStatus.STARTER,
                  PlayerPlayingStatus.REGULAR,
                ].map(v => v.toLowerCase()),
              },
            },
          })}
          {...(defaultHitsPerPage && { defaultHitsPerPage })}
          {...(rest.attributesToRetrieve && {
            attributesToRetrieve: rest.attributesToRetrieve,
          })}
          searchClient={searchClient}
        >
          {!favoritesWatchlist && (
            <FavoriteFilterChangeNotifier onChange={setOnlyFollowed} />
          )}
          <PlayerSearchLayout
            cardFilters={cardFilters}
            dropdownFilters={dropdownFilters}
            sport={sport}
            favoritesWatchlist={favoritesWatchlist}
            {...rest}
          >
            {children}
          </PlayerSearchLayout>
        </InstantCardSearch>
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};

export const BackendAdvancedPlayerSearch = ({
  children,
  sport,
  analyticsTags,
  defaultFilters,
  getOptionalFilters,
  defaultSort,
  initialIndexUIState,
  cardFilters,
  dropdownFilters,
  defaultHitsPerPage,
  so5FixtureSlug = '',
  fetchPolicy,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  withStartersOn = true,
  favoritesWatchlist = false,
  ...rest
}: FullProps) => {
  const { statsManager } = useManagePlayerSearchView();
  const defaultAverageLimit = useDefaultAverageLimit();
  const { language } = useIntlContext();
  const index = defaultSort || rest.sorts[0];
  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();
  const {
    so5: { so5Competitions },
  } = useConfigContext();

  const Wrapper = useCallback(
    ({ children: c }: { children: ReactNode }) => (
      <CurrentMarketplaceUserProvider sport={sport}>
        {c}
      </CurrentMarketplaceUserProvider>
    ),
    [sport]
  );

  const [searchParams, setSearchParams] = useSearchParams();

  let sorts: PrintablePlayerSortName[] =
    (searchParams.get('s')?.split(',') as PrintablePlayerSortName[]) ?? [];
  if (sorts.length === 0) {
    sorts = [index];
  }

  const query = searchParams.get('q') ?? '';

  const page = Number.parseInt(searchParams.get('p') ?? '1', 10);

  const CONFIGURED_FILTERS = Object.entries(BACKEND_FILTERS).map(
    ([urlAttribute, { api: apiAttribute, type }]) =>
      [
        urlAttribute,
        typeof apiAttribute === 'string'
          ? apiAttribute
          : apiAttribute(language),
        type,
      ] as const
  );

  const urlToApi = Object.fromEntries(CONFIGURED_FILTERS);
  const urlToType = Object.fromEntries(
    CONFIGURED_FILTERS.map(e => [e[0], e[2]])
  );

  const apiToUrl = Object.fromEntries(
    CONFIGURED_FILTERS.map(e => e.slice(0, 2).reverse())
  );

  const activeFacets = Object.fromEntries(
    CONFIGURED_FILTERS.map(e => [
      e[0],
      searchParams
        .getAll(e[0])
        .flatMap(e2 =>
          urlToType[e[0]] === 'numeric'
            ? decodeURIComponent(e2).split(':')
            : e2.split(',')
        )
        .map(decodeURIComponent),
    ])
  );

  const onlyFollowed = Boolean(
    favoritesWatchlist || activeFacets.ff[0] === 'true'
  );

  const { data, loading, previousData, refetch } = useQuery(
    ADVANCED_PLAYERS_SEARCH_QUERY,
    {
      variables: {
        query,
        page,
        pageSize: 20,
        advancedFilters: [
          `sport:${sport.toLowerCase()}`,
          ...(defaultFilters ?? []),
          ...(activeFacets.lf.filter(Boolean).length
            ? [
                so5Competitions.find(e => e.slug === activeFacets.lf[0])
                  ?.algoliaFilters,
              ]
                .filter(Boolean)
                .map(e => `(${e})`)
            : []),
        ].join(' AND '),
        sorts: sorts.map(s => {
          const [direction, alias] = s.split('-');
          const field = FIELDS[alias as any] as string;
          return { field, direction: direction as SearchSortDirection };
        }),
        refinements: Object.entries(
          activeFacets
        ).flatMap<SearchRefinementInput>(([k, v]) => {
          if (!v.length || urlToType[k] === 'virtual') return [];
          if (urlToType[k] === 'numeric') {
            return v
              .map(
                (value, i) =>
                  value && {
                    field: urlToApi[k],
                    operator: i
                      ? SearchOperator.LESS_THAN_OR_EQUAL
                      : SearchOperator.GREATER_THAN_OR_EQUAL,
                    values: [{ integerValue: Number.parseInt(value, 10) }],
                  }
              )
              .filter(Boolean);
          }
          return {
            field: urlToApi[k],
            operator: SearchOperator.EQUAL,
            values: v.map(vv => ({ stringValue: vv })),
          };
        }),
        facets: CONFIGURED_FILTERS.filter(
          e => urlToType[e[0]] !== 'virtual'
        ).map(e => {
          return {
            field: e[1],
            limit: 20,
            type: SearchFaceting.DISJUNCTIVE,
          };
        }),
        rarity: statsManager[PlayerSearchViewAttributes.RARITY],
        inSeason:
          statsManager[PlayerSearchViewAttributes.SEASONALITY] ===
          So5LeaderboardSeasonality.IN_SEASON,
        onlyPrimary: false,
        onlyFollowed,
        averageLimit: defaultAverageLimit,
        so5FixtureSlug,
        withoutSo5Fixture: !so5FixtureSlug,
      },
    }
  );

  const refinements = Object.entries(
    activeFacets
  ).flatMap<SearchRefinementInput>(([k, v]) => {
    if (!v.length || urlToType[k] === 'virtual') return [];
    if (urlToType[k] === 'numeric') {
      return v
        .map(
          (value, i) =>
            value && {
              field: urlToApi[k],
              operator: i
                ? SearchOperator.LESS_THAN_OR_EQUAL
                : SearchOperator.GREATER_THAN_OR_EQUAL,
              values: [{ integerValue: Number.parseInt(value, 10) }],
            }
        )
        .filter(Boolean);
    }
    return {
      field: urlToApi[k],
      operator: SearchOperator.EQUAL,
      values: v.map(vv => ({ stringValue: vv })),
    };
  });

  const [search] = useLazyQuery(
    DEFAULT_PRINTABLE_PLAYER_FACET_VALUES_SEARCH_QUERY,
    {
      variables: {
        query,
        advancedFilters: [
          `sport:${sport.toLowerCase()}`,
          ...(defaultFilters ?? []),
          ...(activeFacets.lf.filter(Boolean).length
            ? [
                so5Competitions.find(e => e.slug === activeFacets.lf[0])
                  ?.algoliaFilters,
              ]
            : []),
        ].join(' AND '),
        refinements,
        onlyFollowed,
        // We'll override these variables when calling search
        field: '',
        facetQuery: '',
      },
    }
  );

  const safeData = data ?? previousData;

  return (
    <ConditionalWrapper wrap={!currentMarketplaceUserContext} Wrapper={Wrapper}>
      <SearchCardsContextProvider commonMarket legacyFilters={false}>
        <GQLSearchContext
          value={{
            query,
            search,
            getRefinementType: k => urlToType[k],
            getRefinementApiAttribute: k => urlToApi[k],
            refinements,
            setSearchParams,
            page,
            pages: safeData?.searchPlayers.nbPages ?? 0,
            nbHits: safeData?.searchPlayers.nbHits ?? 0,
            advancedFilters: '', // TODO ADVANCED_FILTERS,
            onSaleOnly: true,
            activeFacets,
            sorts,
            facets: Object.fromEntries(
              [
                {
                  field: 'lf',
                  values: so5Competitions
                    .filter(comp => comp.scoutable)
                    .map(comp => ({
                      value: comp.slug,
                      label: comp.displayName,
                      count: 1,
                    })),
                },
                ...(safeData?.searchPlayers.facets ?? []),
              ]?.map(({ field, ...rest2 }) => [
                apiToUrl[field] ?? field,
                rest2,
              ]) ?? []
            ),
          }}
        >
          {/* {!favoritesWatchlist && (
            <FavoriteFilterChangeNotifier onChange={setOnlyFollowed} />
          )} */}
          <BackendPlayerSearchLayout
            cardFilters={cardFilters}
            dropdownFilters={dropdownFilters}
            sport={sport}
            favoritesWatchlist={favoritesWatchlist}
            items={safeData?.searchPlayers.commonPlayerHits ?? []}
            loading={loading}
            refetch={refetch}
            {...rest}
          >
            {children}
          </BackendPlayerSearchLayout>
        </GQLSearchContext>
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};
